import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Gauge, Compass, ChevronLeft, ChevronRight, Home, Users, Search,
  Handshake, CalendarDays, Briefcase, Sparkles,
} from "lucide-react";
import { PageShell } from "@/components/Navbar";
import { VerificationBanner } from "@/components/VerificationBanner";

/* ------------------------------------------------------------------ *
 * The PropertyConnect Playbook — authenticated "Discover" page.
 * A single manual carousel of seven goal-oriented slides that route to
 * EXISTING destinations. No feeds, no recommendation engine, no tracking.
 * ------------------------------------------------------------------ */

const IMG = (n) => `${process.env.PUBLIC_URL || ""}/playbook/slide${n}.webp`;

// goal ids map selectors <-> slides. null = reachable only via carousel/dots/previews.
const SLIDES = [
  {
    n: 1, goal: "find-demand", title: "Have a property but no buyer?",
    copy: "Publish your property for Sale, Rent, or Stays with clear details, then share its link with your network.",
    steps: ["Publish your inventory", "Complete its details", "Share its link"],
    primary: { label: "Add a Property", to: "/have-property" },
    secondary: { label: "Explore Buyer Needs", to: "/buyer-needs" },
  },
  {
    n: 2, goal: "find-property", title: "Have a buyer but no suitable inventory?",
    copy: "Record the property requirements and explore available inventory to find suitable options.",
    steps: ["Define the requirements", "Explore Marketplace", "Review suitable options"],
    primary: { label: "Explore Marketplace", to: "/marketplace" },
    secondary: { label: "Post a Buyer Need", to: "/have-buyer" },
  },
  {
    n: 3, goal: null, title: "Someone may need your property",
    copy: "Explore active Buyer Needs. Compare location, budget, and requirements, then respond with suitable inventory.",
    steps: ["Browse Buyer Needs", "Compare requirements", "Respond with a suitable listing"],
    primary: { label: "Explore Buyer Needs", to: "/buyer-needs" },
  },
  {
    n: 4, goal: "build-connections", title: "Your next deal may start with a connection",
    copy: "Discover real estate professionals, explore their profiles, and build relationships for future collaboration.",
    steps: ["Discover professionals", "Review their profiles", "Send a connection request"],
    primary: { label: "Discover Professionals", to: "/connections" },
  },
  {
    n: 5, goal: null, title: "Turn interest into a next step",
    copy: "Use Leads to organize inquiries, record your next steps, and keep follow-ups moving.",
    steps: ["Review inquiries", "Record the next step", "Follow up"],
    primary: { label: "Open My Command Center", to: "/command-center" },
    secondary: { label: "Open Leads", to: "/leads" },
  },
  {
    n: 6, goal: "promote-event", title: "Bring people together",
    copy: "Publish open houses and real estate events, share the invitation, and manage RSVPs.",
    steps: ["Publish an event", "Share the invitation", "Review RSVPs"],
    primary: { label: "Explore Events", to: "/marketplace?mode=events" },
    secondary: { label: "Post an Event", to: "/post-event" },
  },
  {
    n: 7, goal: "recruit-talent", title: "Build your sales team",
    copy: "Publish recruitment opportunities and review applicants through the existing recruitment workflow.",
    steps: ["Publish an opportunity", "Share the post", "Review applicants"],
    primary: { label: "Explore Recruitment", to: "/marketplace?mode=recruitment" },
    secondary: { label: "Post Recruitment", to: "/post-recruitment" },
  },
];

const GOALS = [
  { id: "find-property", label: "Find a property", icon: Search, index: 1 },
  { id: "find-demand", label: "Find demand", icon: Home, index: 0 },
  { id: "build-connections", label: "Build connections", icon: Handshake, index: 3 },
  { id: "promote-event", label: "Promote an event", icon: CalendarDays, index: 5 },
  { id: "recruit-talent", label: "Recruit talent", icon: Briefcase, index: 6 },
];

const PREVIEW_INDEXES = [1, 3, 5]; // Slides 2, 4, 6

const TRY_STEPS = ["Publish a property", "Complete its details", "Share its link", "Review Buyer Needs"];

function StepList({ steps }) {
  return (
    <ol className="mt-5 space-y-2" data-testid="slide-steps">
      {steps.map((s, i) => (
        <li key={s} className="flex items-center gap-3 text-sm text-slate-700">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#EDF3FA] text-[#17365D] text-xs font-bold">{i + 1}</span>
          {s}
        </li>
      ))}
    </ol>
  );
}

function ActionButton({ action, variant, testid }) {
  const base = "inline-flex items-center justify-center gap-2 rounded-xl h-11 px-5 text-sm font-semibold transition-colors";
  const cls = variant === "primary"
    ? `${base} bg-[#0F2C59] text-white hover:bg-[#0F2C59]/90`
    : `${base} border border-[#0F2C59]/25 text-[#0F2C59] hover:bg-[#0F2C59]/5`;
  return (
    <Link to={action.to} data-testid={testid} className={cls}>
      {action.label}{variant === "primary" && <ArrowRight className="h-4 w-4" />}
    </Link>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const touchX = useRef(null);
  const slide = SLIDES[index];
  const activeGoal = slide.goal;

  const go = useCallback((i) => setIndex(((i % SLIDES.length) + SLIDES.length) % SLIDES.length), []);
  const prev = useCallback(() => go(index - 1), [go, index]);
  const next = useCallback(() => go(index + 1), [go, index]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
  };
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx > 40) prev(); else if (dx < -40) next();
    touchX.current = null;
  };

  // Keep document focus manageable but don't auto-rotate.
  useEffect(() => { /* no auto-rotation by design */ }, []);

  return (
    <PageShell testid="dashboard-page">
      <VerificationBanner />

      {/* Header — intro left, premium Command Center card right (stacks on mobile) */}
      <div className="grid lg:grid-cols-[1fr_auto] gap-6 lg:gap-10 lg:items-center">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C79A3B] mb-2" data-testid="playbook-eyebrow">
            <Compass className="h-3.5 w-3.5" /> The PropertyConnect Playbook
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#17365D] leading-[1.1] max-w-2xl" data-testid="playbook-headline">
            Your next opportunity starts with a smart move.
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-3 max-w-xl">Choose a goal. See how PropertyConnect helps. Take the next step.</p>
        </div>
        <div data-testid="command-center-card"
          className="w-full lg:w-[340px] rounded-2xl bg-[#0F2C59] ring-1 ring-[#C79A3B]/50 shadow-[0_20px_50px_-20px_rgba(15,44,89,0.6)] p-6">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#C79A3B]/15 ring-1 ring-[#C79A3B]/40 mb-4">
            <Gauge className="h-5 w-5 text-[#C79A3B]" />
          </span>
          <h2 className="font-display text-xl font-bold text-white">My Command Center</h2>
          <p className="text-sm text-white/70 mt-1.5 leading-relaxed">Manage your listings, leads, follow-ups, and performance.</p>
          <Link to="/command-center" data-testid="dashboard-command-center-shortcut"
            className="mt-5 inline-flex items-center justify-center gap-1.5 w-full rounded-xl bg-[#C79A3B] hover:bg-[#b98d33] text-[#0F2C59] font-semibold text-sm px-4 py-2.5 transition-colors">
            Open Command Center <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Goal selectors */}
      <div className="mt-7 flex flex-wrap gap-2" data-testid="goal-selectors" role="tablist" aria-label="Choose a goal">
        {GOALS.map((g) => {
          const active = activeGoal === g.id;
          return (
            <button key={g.id} type="button" role="tab" aria-selected={active}
              data-testid={`goal-${g.id}`} onClick={() => go(g.index)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors border ${active ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "bg-white text-[#17365D] border-[#DCE5EF] hover:bg-[#F2F8FE]"}`}>
              <g.icon className="h-4 w-4" />{g.label}
            </button>
          );
        })}
      </div>

      {/* Carousel */}
      <div
        className="mt-6 rounded-3xl border border-[#E3EAF3] bg-white p-4 sm:p-6 lg:p-8 shadow-[0_18px_50px_-30px_rgba(23,54,93,0.4)]"
        data-testid="playbook-carousel"
        role="group" aria-roledescription="carousel" aria-label="PropertyConnect Playbook"
        tabIndex={0} onKeyDown={onKeyDown}
      >
        <div
          key={index}
          className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-center animate-in fade-in duration-300 motion-reduce:animate-none"
          data-testid="playbook-slide" data-slide-index={index}
          onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
        >
          {/* Artwork (kept separate from text/controls) */}
          <div className="rounded-2xl overflow-hidden bg-[#EDF3FA] ring-1 ring-[#E3EAF3] order-1">
            <img src={IMG(slide.n)} alt={slide.title} loading="eager"
              className="w-full h-auto block" data-testid={`slide-image-${slide.n}`} />
          </div>

          {/* Content */}
          <div className="order-2">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#C79A3B]">Slide {slide.n} of {SLIDES.length}</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#17365D] mt-2 leading-tight" data-testid="slide-title">{slide.title}</h2>
            <p className="text-slate-600 text-sm sm:text-base mt-3 leading-relaxed">{slide.copy}</p>
            <StepList steps={slide.steps} />
            <div className="mt-7 flex flex-col sm:flex-row flex-wrap gap-3">
              <ActionButton action={slide.primary} variant="primary" testid="slide-primary" />
              {slide.secondary && <ActionButton action={slide.secondary} variant="secondary" testid="slide-secondary" />}
            </div>
          </div>
        </div>

        {/* Controls — outside artwork */}
        <div className="mt-7 flex items-center justify-between gap-3 border-t border-[#EDF3FA] pt-5">
          <button type="button" onClick={prev} data-testid="carousel-prev" aria-label="Previous slide"
            className="h-10 w-10 rounded-full border border-[#DCE5EF] text-[#17365D] flex items-center justify-center hover:bg-[#F2F8FE] transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2" data-testid="slide-selectors">
            {SLIDES.map((s, i) => (
              <button key={s.n} type="button" onClick={() => go(i)}
                data-testid={`slide-selector-${s.n}`} aria-label={`Go to slide ${s.n}: ${s.title}`} aria-current={i === index}
                className={`h-2.5 rounded-full transition-all ${i === index ? "w-7 bg-[#0F2C59]" : "w-2.5 bg-[#CBD9EA] hover:bg-[#9FB6D4]"}`} />
            ))}
          </div>
          <button type="button" onClick={next} data-testid="carousel-next" aria-label="Next slide"
            className="h-10 w-10 rounded-full border border-[#DCE5EF] text-[#17365D] flex items-center justify-center hover:bg-[#F2F8FE] transition-colors">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Compact previews (Slides 2, 4, 6) */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="playbook-previews">
        {PREVIEW_INDEXES.map((i) => {
          const s = SLIDES[i];
          return (
            <button key={s.n} type="button" onClick={() => go(i)} data-testid={`preview-card-${s.n}`}
              className="group text-left rounded-2xl border border-[#E3EAF3] bg-white overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C79A3B]">
              <div className="overflow-hidden bg-[#EDF3FA]">
                <img src={IMG(s.n)} alt={s.title} loading="lazy" className="w-full h-auto block group-hover:scale-[1.03] transition-transform duration-500" />
              </div>
              <div className="p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#C79A3B]">Slide {s.n}</div>
                <div className="font-display font-bold text-[#17365D] text-sm mt-1 leading-snug">{s.title}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Try this today */}
      <div className="mt-6 rounded-3xl border border-[#E3EAF3] bg-[#F7FAFE] p-5 sm:p-7" data-testid="try-today-panel">
        <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
          <div className="lg:flex-1">
            <h3 className="font-display text-xl font-bold text-[#17365D] flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#C79A3B]" /> Try this today.
            </h3>
            <p className="text-slate-600 text-sm mt-2">Give one property a better chance to be discovered.</p>
            <ol className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-2">
              {TRY_STEPS.map((s, i) => (
                <li key={s} className="flex items-center gap-2 text-sm text-slate-700">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white border border-[#DCE5EF] text-[#17365D] text-xs font-bold">{i + 1}</span>
                  {s}
                  {i < TRY_STEPS.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-slate-400 ml-1" />}
                </li>
              ))}
            </ol>
          </div>
          <button type="button" onClick={() => navigate("/have-property")} data-testid="try-today-action"
            className="inline-flex items-center justify-center gap-2 rounded-xl h-12 px-6 text-sm font-semibold bg-[#C79A3B] text-white hover:bg-[#C79A3B]/90 transition-colors shrink-0">
            Start with a Property <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </PageShell>
  );
}
